/* eslint-disable import/no-cycle */
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import * as types from '../../referrals/actionTypes';
import { startLoader, stopLoader } from '../../../utility/commonFunction';
import {
  setReferralsList,
  setReferralsListFailed,
  addReferralSuccess,
  addReferralFailed,
  getReferralsList,
  setReferralDetails,
  setReferralDetailsFailed,
  updateReferralStatusSuccess,
  updateReferralStatusFailed,
} from '../../referrals/actions';
import {
  getReferralsListApi,
  getReferralDetailsApi,
  getReferralKpisApi,
  addReferralApi,
  updateReferralStatusApi,
} from '../requests/referralRequest';

function selectIsSuperAdmin(state) {
  const role = state?.auth?.user?.role || state?.auth?.me?.user?.roles?.[0];
  return role === 'super_admin';
}

function selectIsStaff(state) {
  const role = state?.auth?.user?.role || state?.auth?.me?.user?.roles?.[0];
  return role === 'staff';
}

function referralKpiRecordsToObject(records) {
  if (!Array.isArray(records)) return {};
  return records.reduce((acc, row) => {
    if (row?.kpi_name != null) acc[row.kpi_name] = row.value;
    return acc;
  }, {});
}

function normalizeReferralDetailsResponse(apiData) {
  const row = apiData?.data;
  if (!row || row.current_status || !row.status) return apiData;
  return {
    ...apiData,
    data: { ...row, current_status: row.status },
  };
}

function pickFirstFormError(formErrors) {
  if (!formErrors || typeof formErrors !== 'object') return null;
  const values = Object.values(formErrors);
  if (!values.length) return null;
  const first = values[0];
  if (Array.isArray(first)) return first[0] || null;
  return first || null;
}

function resolveBackendMessage(payload) {
  if (!payload || typeof payload !== 'object') return null;
  return (
    payload?.message ||
    payload?.errors?.message ||
    pickFirstFormError(payload?.errors?.form_errors) ||
    pickFirstFormError(payload?.form_errors) ||
    null
  );
}

function hasBackendErrors(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const errors = payload?.errors;
  if (Array.isArray(errors)) return errors.length > 0;
  if (errors && typeof errors === 'object') {
    const formError = pickFirstFormError(errors?.form_errors);
    return Boolean(errors?.message || formError);
  }
  return Boolean(errors);
}

/** Unifies merchant `{ data: { list, kpis } }` and super_admin `{ data: [...] }` list responses. */
function normalizeReferralsListResponse(apiData, kpiObject = {}, isSuperAdmin = false) {
  if (!apiData) return apiData;
  const rawData = apiData.data;
  const list = Array.isArray(rawData) ? rawData : rawData?.list ?? [];
  const normalizedList = isSuperAdmin
    ? list.map((item) => {
        if (!item || typeof item !== 'object') return item;
        const firstName = item.first_name ?? '';
        const lastName = item.last_name ?? '';
        const derivedName = `${firstName} ${lastName}`.trim();
        return {
          ...item,
          name: item.name ?? (derivedName || firstName || lastName || '-'),
        };
      })
    : list;
  const embeddedKpis =
    (!Array.isArray(rawData) && rawData?.kpis) || apiData.meta_params?.kpis || {};
  return {
    ...apiData,
    data: {
      ...(typeof rawData === 'object' && rawData !== null && !Array.isArray(rawData) ? rawData : {}),
      list: normalizedList,
      kpis: { ...embeddedKpis, ...kpiObject },
    },
  };
}

function* getReferralsListHandler(action) {
  try {
    yield startLoader('transparent');
    const payload = action.payload || {};
    const isSuperAdmin =
      payload.isSuperAdmin !== undefined ? payload.isSuperAdmin : yield select(selectIsSuperAdmin);
    const isStaff = payload.isStaff !== undefined ? payload.isStaff : yield select(selectIsStaff);
    const { isSuperAdmin: _omit, isStaff: _omitStaff, ...listQuery } = payload;
    const { data } = yield call(getReferralsListApi, { ...listQuery, isSuperAdmin, isStaff });
    let normalized = normalizeReferralsListResponse(data, {}, isSuperAdmin);
    if (isSuperAdmin || isStaff) {
      try {
        const kpiRes = yield call(getReferralKpisApi);
        const kpiObj = referralKpiRecordsToObject(kpiRes?.data?.data);
        normalized = normalizeReferralsListResponse(data, kpiObj, true);
      } catch {
        normalized = normalizeReferralsListResponse(data, {}, true);
      }
    }
    yield put(setReferralsList(normalized));
    if (data?.errors?.message) {
      yield toast.error(data.errors.message);
    }
  } catch (err) {
    yield put(setReferralsListFailed());
    const message = err?.response?.data?.errors?.message || err?.message || 'Failed to load referrals';
    yield toast.error(message);
  } finally {
    yield stopLoader('transparent');
  }
}

function* getReferralDetailsHandler(action) {
  const { id, isSuperAdmin: payloadSuperAdmin, isStaff: payloadIsStaff } = action.payload || {};
  if (!id) return;
  const isSuperAdmin =
    payloadSuperAdmin !== undefined ? payloadSuperAdmin : yield select(selectIsSuperAdmin);
  const isStaff = payloadIsStaff !== undefined ? payloadIsStaff : yield select(selectIsStaff);
  try {
    yield startLoader('transparent');
    const { data } = yield call(getReferralDetailsApi, id, isSuperAdmin, isStaff);
    yield put(setReferralDetails(normalizeReferralDetailsResponse(data)));
    if (data?.errors?.message) {
      yield toast.error(data.errors.message);
    }
  } catch (err) {
    yield put(setReferralDetailsFailed());
    const message = err?.response?.data?.errors?.message || err?.message || 'Failed to load referral details';
    yield toast.error(message);
  } finally {
    yield stopLoader('transparent');
  }
}

function* addReferralHandler(action) {
  try {
    const { data } = yield call(addReferralApi, action.payload);
    const backendMessage = resolveBackendMessage(data);
    if (data?.status === false || hasBackendErrors(data) || pickFirstFormError(data?.form_errors)) {
      yield toast.error(backendMessage || 'Failed to add referral');
      yield put(addReferralFailed());
    } else {
      yield put(addReferralSuccess());
      yield toast.success(backendMessage || 'Referral submitted successfully.');
      const isSuperAdmin = yield select(selectIsSuperAdmin);
      const isStaff = yield select(selectIsStaff);
      yield put(getReferralsList({ isSuperAdmin, isStaff }));
    }
  } catch (err) {
    yield put(addReferralFailed());
    const responseData = err?.response?.data;
    const message = resolveBackendMessage(responseData) || err?.message || 'Failed to add referral';
    yield toast.error(message);
  }
}

function* updateReferralStatusHandler(action) {
  const { id, payload } = action.payload || {};
  if (!id || !payload) return;
  try {
    yield startLoader('transparent');
    const { data } = yield call(updateReferralStatusApi, id, payload);
    const backendMessage = resolveBackendMessage(data);
    if (data?.status === false || hasBackendErrors(data) || pickFirstFormError(data?.form_errors)) {
      yield put(updateReferralStatusFailed());
      yield toast.error(backendMessage || 'Failed to update referral status');
      return;
    }
    yield put(updateReferralStatusSuccess());
    const isSuperAdmin = yield select(selectIsSuperAdmin);
    const isStaff = yield select(selectIsStaff);
    const detailsResponse = yield call(getReferralDetailsApi, id, isSuperAdmin, isStaff);
    yield put(setReferralDetails(normalizeReferralDetailsResponse(detailsResponse?.data)));
    yield toast.success(backendMessage || 'Referral status has been updated successfully.');
  } catch (err) {
    yield put(updateReferralStatusFailed());
    const responseData = err?.response?.data;
    const message = resolveBackendMessage(responseData) || err?.message;
    yield toast.error(message || 'Failed to update referral status');
  } finally {
    yield stopLoader('transparent');
  }
}

export default function* referralSaga() {
  yield takeLatest(types.GET_REFERRALS_LIST, getReferralsListHandler);
  yield takeLatest(types.GET_REFERRAL_DETAILS, getReferralDetailsHandler);
  yield takeLatest(types.ADD_REFERRAL, addReferralHandler);
  yield takeLatest(types.UPDATE_REFERRAL_STATUS, updateReferralStatusHandler);
}
